/**
 * Auth Service — Cognito authentication via AWS Amplify.
 *
 * Provides login, logout, token access, and a shared getAuthHeaders()
 * function that all API modules should use for authenticated requests.
 *
 * Amplify automatically handles:
 *   - Token storage (in-memory + browser storage)
 *   - Silent token refresh via Refresh Token
 *   - Token expiration detection
 */

import {
  signIn,
  signOut,
  getCurrentUser,
  fetchAuthSession,
  resetPassword,
  confirmResetPassword,
} from 'aws-amplify/auth';
/* session.js does not import this module (it talks to Amplify directly), so this
   direction creates no cycle. */
import { getSessionAuthSub } from './session.js';

// ---------------------------------------------------------------------------
// Authentication
// ---------------------------------------------------------------------------

/**
 * Sign in directly with Cognito via Amplify.
 * No backend Lambda involved in authentication.
 *
 * @param {string} username
 * @param {string} password
 * @returns {Promise<{success: boolean, challenge?: object, error?: string}>}
 */
export async function login(username, password) {
  try {
    // Clear any stale Amplify auth state from a previous session or
    // incomplete sign-in flow to avoid "already a signed in user" errors
    await signOut();
  } catch {
    // Ignore — signOut throws if nobody is signed in
  }
  try {
    const result = await signIn({ username, password });
    if (result.isSignedIn) {
      return { success: true };
    }
    // Handle challenges (NEW_PASSWORD_REQUIRED, MFA, etc.)
    return { success: false, challenge: result.nextStep };
  } catch (err) {
    return { success: false, error: err.message || 'Authentication failed' };
  }
}

/**
 * Sign out — clears all Amplify-managed tokens.
 */
export async function logout() {
  try {
    await signOut();
  } catch (err) {
    console.error('[auth] Sign out error:', err);
  }
}

// ---------------------------------------------------------------------------
// Password Reset (ForgotPassword flow)
// ---------------------------------------------------------------------------

/**
 * Step 1: ask Cognito to email a reset code to the user.
 * Returns a masked destination (e.g. "t***@p***.com") on success so the
 * UI can confirm where the code went.
 */
export async function requestPasswordReset(username) {
  try {
    const result = await resetPassword({ username });
    const destination = result?.nextStep?.codeDeliveryDetails?.destination || null;
    return { success: true, destination };
  } catch (err) {
    return { success: false, error: mapResetError(err) };
  }
}

/**
 * Step 2: submit the code + new password to finish the reset.
 */
export async function submitPasswordReset(username, code, newPassword) {
  try {
    await confirmResetPassword({ username, confirmationCode: code, newPassword });
    return { success: true };
  } catch (err) {
    return { success: false, error: mapResetError(err) };
  }
}

/**
 * Translate Cognito error names into user-facing copy. We intentionally do
 * NOT distinguish "user not found" from a successful request — leaking that
 * lets attackers enumerate accounts.
 */
function mapResetError(err) {
  switch (err?.name) {
    case 'UserNotFoundException':
    case 'InvalidParameterException':
      return 'If an account exists for that username, a code has been sent.';
    case 'LimitExceededException':
    case 'TooManyRequestsException':
      return 'Too many attempts. Please try again later.';
    case 'CodeMismatchException':
      return 'Incorrect code. Please check your email and try again.';
    case 'ExpiredCodeException':
      return 'That code has expired. Please request a new one.';
    case 'InvalidPasswordException':
      return 'Password must be at least 6 characters.';
    case 'NotAuthorizedException':
      return 'This account cannot reset its password. Contact your administrator.';
    default:
      return err?.message || 'Something went wrong. Please try again.';
  }
}

// ---------------------------------------------------------------------------
// Token Access
// ---------------------------------------------------------------------------

/**
 * Get the current ID token for API requests.
 * Amplify automatically refreshes expired tokens using the Refresh Token.
 *
 * @returns {Promise<string|null>} The ID token string, or null if not authenticated
 */
export async function getIdToken() {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() || null;
  } catch {
    return null;
  }
}

/**
 * The authenticated identity as Cognito reports it, or null.
 *
 * Read straight off the ID token, so it is the identity the API Gateway
 * authorizer will see. Deliberately NOT derived from anything the user typed:
 * Cognito can resolve a sign-in to a different account than the string entered
 * (alias attributes), and the portal previously stored the typed string as the
 * session username, which is why a session could display one user while
 * authenticating as another.
 *
 * @returns {Promise<{sub: string|null, username: string|null}|null>}
 */
export async function getAuthIdentity() {
    try {
        const session = await fetchAuthSession();
        const payload = session?.tokens?.idToken?.payload;
        if (!payload) return null;
        return {
            sub: payload.sub ?? null,
            username: payload['cognito:username'] ?? null,
        };
    } catch {
        return null;
    }
}

/**
 * Check if user is currently authenticated with valid tokens.
 *
 * @returns {Promise<boolean>}
 */
export async function isAuthenticated() {
  try {
    await getCurrentUser();
    return true;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Shared Auth Headers
// ---------------------------------------------------------------------------

/**
 * Returns standard auth headers for all PalCare API requests.
 * Replaces every `'x-amz-security-token': import.meta.env.VITE_AUTH_TOKEN`
 * with a per-user JWT Bearer token.
 *
 * Usage:
 *   const headers = await getAuthHeaders();
 *   fetch(url, { method: 'POST', headers, body });
 *
 * @returns {Promise<object>} Headers object with Content-Type and Authorization
 */
export async function getAuthHeaders() {
    const session = await fetchAuthSession().catch(() => null);
    const idToken = session?.tokens?.idToken;
    const sub = idToken?.payload?.sub ?? null;

    /* Identity guard. The session blob records the sub it was built for; if the
       live token now belongs to someone else, this request would authorise as one
       user while carrying another user's id and permissions in its body. Refuse it
       and force a re-login rather than let the two identities cross.

       Only fires when BOTH are known: during login the session does not exist yet
       (that flow calls /session with getIdToken() directly, not through here), and
       a request with no token fails on its own further down. */
    const sessionSub = getSessionAuthSub();
    if (sessionSub && sub && sessionSub !== sub) {
        handleIdentityMismatch(sessionSub, sub);
        throw new Error('Session identity no longer matches the signed-in user.');
    }

    return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken?.toString() ?? null}`,
    };
}

/**
 * Tear down a session whose identity has drifted from the token.
 *
 * Clears the session blob and reloads to the login screen. Guarded so a burst of
 * in-flight requests all hitting the mismatch at once produces one teardown, not
 * one per request.
 */
let _mismatchHandled = false;
function handleIdentityMismatch(sessionSub, tokenSub) {
    if (_mismatchHandled) return;
    _mismatchHandled = true;
    /* Subs are UUIDs tied to a Cognito account, not secrets, but there is no
       reason to print them in full — the first segment is enough to correlate
       with a CloudWatch event while investigating. */
    const short = (v) => (typeof v === 'string' ? v.split('-')[0] : String(v));
    console.error(
        `[auth] Signed-in user changed since this session was created `
        + `(session ${short(sessionSub)} vs token ${short(tokenSub)}). Clearing session.`,
    );
    try {
        sessionStorage.clear();
    } catch {
        /* ignore — the reload below still lands on the login screen */
    }
    window.location.href = '/';
}
